/**
 * LearnerOnboardingFlow — calm, conversational 5-step entry into
 * the platform. Replaces the previous form-style learner onboarding.
 *
 * Operator brief (May 19, 2026 — emotional foundation):
 *   "The onboarding should feel like 'Someone is guiding me safely
 *    into my journey' — not 'I'm filling out forms'. Progressive,
 *    conversational, reassuring, lightweight, easy to complete.
 *    One focus per screen. Black space is intentional."
 *
 * Step map:
 *   1. Welcome              (Crown · "Welcome to your learning journey")
 *   2. A few basics about you  (name · age · driving experience)
 *   3. Your goals               (multi-select pass test / confident / refresh)
 *   4. How would you like to learn?  (multi-select step-by-step / visual / short / explore)
 *   5. You're all set          (Crown · "Go to my home")
 *
 * Persistence: answers are stored to `localStorage` under
 * `learner_onboarding_preferences_v1` so the intent-first home can
 * personalise the experience in a follow-up pass. Backend wiring
 * is intentionally deferred per operator scope.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Crown,
  ChevronLeft,
  User as UserIcon,
  Calendar as CalendarIcon,
  Check,
  Mountain,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "learner_onboarding_preferences_v1";

const GOAL_OPTIONS = [
  { id: "pass_test",       label: "Pass my driving test" },
  { id: "confident",       label: "Become a confident driver" },
  { id: "improve_skills",  label: "Improve my skills" },
  { id: "refresh",         label: "Refresh my skills" },
  { id: "other",           label: "Other goal" },
];

const STYLE_OPTIONS = [
  { id: "step_by_step",     label: "Step-by-step guidance" },
  { id: "visual",           label: "Visual learning" },
  { id: "short_simple",     label: "Short & simple" },
  { id: "independent",      label: "I'll explore my way" },
  { id: "instructor_led",   label: "Instructor-supported learning" },
];

const EXPERIENCE_OPTIONS = [
  { id: "none",         label: "I haven't driven yet" },
  { id: "some_lessons", label: "I've had a few lessons" },
  { id: "regular",      label: "I'm taking lessons regularly" },
  { id: "returning",    label: "I'm a returning driver" },
  { id: "skip",         label: "I'd rather not say" },
];

export default function LearnerOnboardingFlow() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    name: "",
    age: "",
    driving_experience: "",
    goals: [],
    learning_styles: [],
  });

  const update = useCallback((patch) => {
    setAnswers((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleMulti = useCallback((field, id) => {
    setAnswers((prev) => {
      const current = prev[field] || [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, [field]: next };
    });
  }, []);

  const persist = useCallback(async () => {
    const payload = {
      ...answers,
      completed_at: new Date().toISOString(),
      version: 1,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) { /* private mode / quota — best-effort */ }
    // Best-effort flag completion server-side so a quick re-login
    // doesn't bounce the learner back through the flow.
    try {
      await axios.post(
        `${API}/api/onboarding/welcome/complete`,
        {},
        { withCredentials: true },
      );
    } catch (_) { /* best-effort — local persistence is the source of truth */ }
  }, [answers]);

  const finish = useCallback(async () => {
    await persist();
    nav("/learn", { replace: true });
  }, [persist, nav]);

  return (
    <div
      data-testid="learner-onboarding-flow"
      data-current-step={step}
      className="min-h-dvh no-overflow-x bg-black text-white flex flex-col"
    >
      <main className="flex-1 flex flex-col items-center px-6 pt-16 pb-12 max-w-md mx-auto w-full">

        {/* Top chrome — back chevron + step dots (steps 2/3/4 only) */}
        {step > 1 && step < 5 && (
          <div className="w-full flex items-center justify-between mb-10">
            <button
              type="button"
              data-testid="onboarding-back-btn"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              aria-label="Back"
              className="p-2 -ml-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <StepDots step={step} />
            <span className="w-7" />
          </div>
        )}

        {step === 1 && <StepWelcome onNext={() => setStep(2)} />}
        {step === 2 && (
          <StepBasics
            answers={answers}
            update={update}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <StepGoals
            selected={answers.goals}
            onToggle={(id) => toggleMulti("goals", id)}
            onNext={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <StepLearningStyle
            selected={answers.learning_styles}
            onToggle={(id) => toggleMulti("learning_styles", id)}
            onNext={() => setStep(5)}
          />
        )}
        {step === 5 && <StepAllSet onFinish={finish} />}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step indicator — three dots, the current step is amber.

function StepDots({ step }) {
  // Steps 2/3/4 → dots 1/2/3 (welcome + all-set don't show dots).
  const dotIdx = step - 2;
  return (
    <div data-testid="onboarding-step-dots" className="flex items-center gap-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`block w-2 h-2 rounded-full ${
            i === dotIdx ? "bg-amber-400" : "bg-zinc-600"
          }`}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Reusable primitives.

function PrimaryButton({ children, onClick, testid, disabled }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`
        mt-10 w-full h-12 rounded-2xl text-black font-extrabold text-sm
        bg-amber-400 hover:bg-amber-300 active:scale-[0.98]
        shadow-[0_0_28px_-6px_rgba(251,191,36,0.55)]
        transition-all
        disabled:opacity-40 disabled:cursor-not-allowed
      `}
    >
      {children}
    </button>
  );
}

function ChipOption({ testid, selected, onClick, icon, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={selected}
      onClick={onClick}
      className={`
        w-full h-12 px-4 rounded-2xl flex items-center gap-3
        border transition-all text-left
        ${selected
          ? "border-amber-400 bg-amber-400/10 text-white shadow-[0_0_16px_-4px_rgba(251,191,36,0.45)]"
          : "border-zinc-700 bg-zinc-900/60 text-zinc-200 hover:border-zinc-500"}
      `}
    >
      <span className="text-zinc-400">{icon}</span>
      <span className="flex-1 text-sm font-semibold">{label}</span>
      {selected && (
        <span className="w-5 h-5 rounded-full bg-amber-400 text-black flex items-center justify-center">
          <Check className="w-3.5 h-3.5" />
        </span>
      )}
    </button>
  );
}

function CalmInput({ testid, value, onChange, icon, placeholder, type = "text", inputMode }) {
  return (
    <div
      className={`
        w-full flex items-center gap-3 px-4 h-12 rounded-2xl
        border border-zinc-700 bg-zinc-900/60
        focus-within:border-zinc-500 focus-within:shadow-[0_0_18px_-6px_rgba(255,255,255,0.25)]
        transition-shadow
      `}
    >
      <span className="text-zinc-400 shrink-0">{icon}</span>
      <input
        data-testid={testid}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-500 text-white"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 1 — Welcome

function StepWelcome({ onNext }) {
  return (
    <div data-testid="onboarding-step-welcome" className="flex flex-col items-center text-center w-full">
      <Crown className="w-10 h-10 text-amber-400" strokeWidth={2.5} />
      <h1
        data-testid="onboarding-welcome-heading"
        className="mt-6 text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight"
      >
        Welcome to your{" "}
        <span className="text-amber-400">learning journey.</span>
      </h1>
      <p className="mt-4 text-sm sm:text-base text-zinc-400">
        We're here to support you every step of the way.
      </p>
      <Mountain
        className="mt-10 w-28 h-28 text-amber-400/80"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <PrimaryButton testid="onboarding-welcome-next-btn" onClick={onNext}>
        Let's get started
      </PrimaryButton>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 2 — A few basics about you

function StepBasics({ answers, update, onNext }) {
  return (
    <div data-testid="onboarding-step-basics" className="flex flex-col items-stretch w-full">
      <h2
        data-testid="onboarding-basics-heading"
        className="text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight text-center"
      >
        A few basics about you.
      </h2>
      <p className="mt-3 text-sm text-zinc-400 text-center">
        This helps us personalise your experience.
      </p>

      <div className="mt-8 space-y-3">
        <CalmInput
          testid="onboarding-basics-name"
          value={answers.name}
          onChange={(v) => update({ name: v })}
          icon={<UserIcon className="w-4 h-4" />}
          placeholder="What should we call you?"
        />
        <CalmInput
          testid="onboarding-basics-age"
          value={answers.age}
          onChange={(v) => update({ age: v.replace(/\D+/g, "") })}
          icon={<CalendarIcon className="w-4 h-4" />}
          placeholder="How old are you?"
          inputMode="numeric"
        />
        <div className="space-y-2">
          <p className="px-1 text-xs text-zinc-500 font-semibold">
            What's your driving experience?
          </p>
          <div className="grid grid-cols-1 gap-2">
            {EXPERIENCE_OPTIONS.map((opt) => (
              <ChipOption
                key={opt.id}
                testid={`onboarding-basics-exp-${opt.id}`}
                selected={answers.driving_experience === opt.id}
                onClick={() => update({ driving_experience: opt.id })}
                label={opt.label}
              />
            ))}
          </div>
        </div>
      </div>

      <PrimaryButton testid="onboarding-basics-next-btn" onClick={onNext}>
        Continue
      </PrimaryButton>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 3 — Your goals

function StepGoals({ selected, onToggle, onNext }) {
  return (
    <div data-testid="onboarding-step-goals" className="flex flex-col items-stretch w-full">
      <h2
        data-testid="onboarding-goals-heading"
        className="text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight text-center"
      >
        What are your goals?
      </h2>
      <p className="mt-3 text-sm text-zinc-400 text-center">
        Choose what you'd like to achieve.
      </p>

      <div className="mt-8 space-y-2">
        {GOAL_OPTIONS.map((opt) => (
          <ChipOption
            key={opt.id}
            testid={`onboarding-goals-${opt.id}`}
            selected={selected.includes(opt.id)}
            onClick={() => onToggle(opt.id)}
            label={opt.label}
          />
        ))}
      </div>

      <PrimaryButton testid="onboarding-goals-next-btn" onClick={onNext}>
        Continue
      </PrimaryButton>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 4 — How would you like to learn?

function StepLearningStyle({ selected, onToggle, onNext }) {
  return (
    <div data-testid="onboarding-step-learning-style" className="flex flex-col items-stretch w-full">
      <h2
        data-testid="onboarding-style-heading"
        className="text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight text-center"
      >
        How would you like to learn?
      </h2>
      <p className="mt-3 text-sm text-zinc-400 text-center">
        We'll support you in the way that works best for you.
      </p>

      <div className="mt-8 space-y-2">
        {STYLE_OPTIONS.map((opt) => (
          <ChipOption
            key={opt.id}
            testid={`onboarding-style-${opt.id}`}
            selected={selected.includes(opt.id)}
            onClick={() => onToggle(opt.id)}
            label={opt.label}
          />
        ))}
      </div>

      <PrimaryButton testid="onboarding-style-next-btn" onClick={onNext}>
        Continue
      </PrimaryButton>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 5 — You're all set

function StepAllSet({ onFinish }) {
  return (
    <div data-testid="onboarding-step-all-set" className="flex flex-col items-center text-center w-full">
      <div className="relative">
        <Crown
          className="w-12 h-12 text-amber-400"
          strokeWidth={2.5}
          data-testid="onboarding-all-set-crown"
        />
        {/* Calm confetti dots — small + soft, not childish */}
        <span className="absolute -top-2 -left-6 w-1.5 h-1.5 rounded-full bg-sky-400" />
        <span className="absolute -top-3 left-10 w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="absolute -top-1 left-16 w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
        <span className="absolute top-4 -right-7 w-1.5 h-1.5 rounded-full bg-amber-300" />
        <span className="absolute top-10 -left-7 w-1.5 h-1.5 rounded-full bg-amber-300" />
      </div>

      <h2
        data-testid="onboarding-all-set-heading"
        className="mt-8 text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight"
      >
        You're all set,{" "}
        <span className="text-amber-400">let's go!</span>
      </h2>
      <p className="mt-4 text-sm sm:text-base text-zinc-400">
        Your journey starts here.
        <br />
        We're excited to have you with us.
      </p>

      <PrimaryButton testid="onboarding-finish-btn" onClick={onFinish}>
        Go to my home
      </PrimaryButton>
    </div>
  );
}

